import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { LineGroup } from '../components/performance/types';

/** Maximum font size in px. */
const MAX_FONT_SIZE = 64;
/** Minimum font size in px — keep text readable. */
const MIN_FONT_SIZE = 12;

/**
 * Measure the width of a single monospace character at 100px in the
 * given font-family. Returns charWidth / fontSize ratio.
 */
function measureCharRatio(fontFamily: string): number {
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.visibility = 'hidden';
  span.style.whiteSpace = 'pre';
  span.style.font = `100px ${fontFamily}`;
  span.textContent = 'M';
  document.body.appendChild(span);
  const ratio = span.getBoundingClientRect().width / 100;
  document.body.removeChild(span);
  return ratio || 0.6;
}

/**
 * Computes a font size (in px) so the longest line in `groups` fits
 * within the container width without wrapping.
 *
 * Uses a callback ref so the ResizeObserver is set up as soon as the
 * DOM element appears (works even if the element mounts after the
 * initial render, e.g. after async data loads).
 */
export function useFitFontSize(groups: LineGroup[]): {
  fontSize: number;
  ref: (node: HTMLElement | null) => void;
} {
  const [containerWidth, setContainerWidth] = useState(0);
  const charRatioRef = useRef(0);
  const roRef = useRef<ResizeObserver | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  // Callback ref — called by React whenever the DOM node mounts/unmounts
  const ref = useCallback((node: HTMLElement | null) => {
    // Clean up previous observer
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }

    nodeRef.current = node;

    if (!node) {
      setContainerWidth(0);
      return;
    }

    // Measure char ratio once
    if (charRatioRef.current === 0) {
      charRatioRef.current = measureCharRatio(
        getComputedStyle(node).fontFamily || "'Courier New', monospace",
      );
    }

    // Observe size changes
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(node);
    roRef.current = ro;

    // Set initial width
    const style = getComputedStyle(node);
    const pl = parseFloat(style.paddingLeft) || 0;
    const pr = parseFloat(style.paddingRight) || 0;
    setContainerWidth(node.clientWidth - pl - pr);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (roRef.current) {
        roRef.current.disconnect();
      }
    };
  }, []);

  // Find the longest line (in characters) across ALL groups
  const maxChars = useMemo(() => {
    let max = 1;
    for (const g of groups) {
      if (g.chordText) max = Math.max(max, g.chordText.length);
      if (g.line.text) max = Math.max(max, g.line.text.length);
    }
    return max;
  }, [groups]);

  // Derive font size
  const fontSize = useMemo(() => {
    if (containerWidth <= 0 || maxChars <= 0) return MAX_FONT_SIZE;
    const ratio = charRatioRef.current || 0.6;
    const computed = containerWidth / (maxChars * ratio);
    return Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, Math.floor(computed)));
  }, [containerWidth, maxChars]);

  return { fontSize, ref };
}
